import { api } from '@/api'
import { BaseApi } from '@/api/BaseApi.ts'
import type {
  InitiativeAddPermissionsPayload,
  InitiativeChangePermissionRolePayload,
  InitiativeData,
  InitiativeDetail,
  InitiativeFetchParams,
  InitiativeFormData,
  InitiativeLastCollect,
  InitiativePermissionsParams,
  InitiativePermissionsResult,
  InitiativeSearchCollectsParams,
  InitiativeSearchCollectsResult,
  InitiativeSearchParams,
  InitiativeSearchResult,
  InitiativeSummary,
  InternalFeaturesPayload,
} from '@/api/Initiative/types.ts'
import type { Paginated, ParamType } from '@/api/types.ts'

class Api extends BaseApi<
  InitiativeDetail,
  Paginated<InitiativeData>,
  InitiativeFormData,
  InitiativeFetchParams
> {
  readonly baseUrl = '/initiatives'

  async getDetail(
    id: ParamType,
    options: { silentForbidden?: boolean } = {}
  ): Promise<InitiativeDetail> {
    const url = this.getBaseUrl(`${this.baseUrl}/:id`, [id])
    const { data } = await api.get<InitiativeDetail>(url, {
      ...this.config,
      suppressForbiddenToast: options.silentForbidden === true,
      skipAuthRedirect: options.silentForbidden === true,
    })
    return data
  }

  async fetchPermissions(
    params: ParamType[],
    fetchParams: InitiativePermissionsParams
  ): Promise<InitiativePermissionsResult> {
    const url = this.buildUrl(
      `${this.baseUrl}/:initiativeId/permissions`,
      params,
      fetchParams
    )
    const { data } = await api.get<InitiativePermissionsResult>(
      url,
      this.config
    )
    return data
  }

  async addPermissions(
    params: ParamType[],
    payload: InitiativeAddPermissionsPayload
  ): Promise<void> {
    const url = this.getBaseUrl(
      this.baseUrl + '/:initiativeId/permissions',
      params
    )
    const { data } = await api.post<void>(url, payload, this.config)
    return data
  }

  async changePermissionRole(
    params: ParamType[],
    payload: InitiativeChangePermissionRolePayload
  ): Promise<void> {
    const url = this.getBaseUrl(
      this.baseUrl + '/:initiativeId/permissions/:userId',
      params
    )
    const { data } = await api.patch<void>(url, payload, this.config)
    return data
  }

  async removePermission(params: ParamType[]): Promise<void> {
    const url = this.getBaseUrl(
      this.baseUrl + '/:initiativeId/permissions/:userId',
      params
    )
    const { data } = await api.delete<void>(url, this.config)
    return data
  }

  async duplicateInitiative(
    params: ParamType[],
    payload: InitiativeFormData
  ): Promise<void> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:initiativeId/duplicate', params)}`
    const { data } = await api.post<void>(url, payload, this.config)
    return data
  }

  async updateInternalFeatures(
    params: ParamType[],
    payload: InternalFeaturesPayload
  ): Promise<void> {
    const url = this.getBaseUrl(
      this.baseUrl + '/:initiativeId/internal-features',
      params
    )
    await api.put<void>(url, payload, this.config)
  }

  async search(
    params: ParamType[],
    searchParams: InitiativeSearchParams
  ): Promise<InitiativeSearchResult[]> {
    const url = this.buildUrl(
      `${this.baseUrl}/:initiativeId/search`,
      params,
      searchParams
    )
    const { data } = await api.get<InitiativeSearchResult[]>(url, this.config)
    return data
  }

  async summary(params: ParamType[]): Promise<InitiativeSummary> {
    const url = this.getBaseUrl(this.baseUrl + '/:initiativeId/summary', params)
    const { data } = await api.get<InitiativeSummary>(url, this.config)
    return data
  }

  async lastCollects(params: ParamType[]): Promise<InitiativeLastCollect[]> {
    const url = this.getBaseUrl(
      this.baseUrl + '/:initiativeId/last-collects',
      params
    )
    const { data } = await api.get<InitiativeLastCollect[]>(url, this.config)
    return data
  }

  async searchCollects(
    params: ParamType[],
    searchParams: InitiativeSearchCollectsParams
  ): Promise<InitiativeSearchCollectsResult> {
    const url = this.buildUrl(
      `${this.baseUrl}/:initiativeId/search-collects`,
      params,
      searchParams
    )
    const { data } = await api.get<InitiativeSearchCollectsResult>(
      url,
      this.config
    )
    return data
  }
}

const InitiativeApi = new Api()

export default InitiativeApi
